export type Principal = {
  userId: string;
  name: string;
  email: string;
  realmRoles: readonly string[];
  clientRoles: readonly string[];
  tenantId: string | null;
  tenantSlug: string | null;
  plan: string | null;
  features: readonly string[];
  subscriptionStatus: string | null;
  expiresAt: number;
};

type Claims = {
  sub?: string;
  name?: string;
  preferred_username?: string;
  email?: string;
  exp?: number;
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
  tenant_id?: string;
  tenant_slug?: string;
  plan?: string;
  features?: string[];
  sub_status?: string;
  organization?: OrganizationClaim;
};

type OrganizationAttributes = Record<string, string[] | string | undefined>;
type OrganizationClaim = readonly (Record<string, OrganizationAttributes> | string)[];

const firstValue = (value: string[] | string | undefined): string | null =>
  Array.isArray(value) ? (value[0] ?? null) : (value ?? null);

const listValue = (value: string[] | string | undefined): readonly string[] =>
  Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean)
      : [];

const organizationOf = (
  claim: OrganizationClaim | undefined,
): { slug: string | null; attributes: OrganizationAttributes } => {
  for (const entry of claim ?? []) {
    if (typeof entry !== 'object') continue;
    for (const [slug, attributes] of Object.entries(entry)) {
      if (typeof attributes === 'object') return { slug, attributes };
    }
  }
  return { slug: null, attributes: {} };
};

const decodeBase64Url = (segment: string): string => {
  const padded = segment
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(segment.length / 4) * 4, '=');
  return new TextDecoder().decode(Uint8Array.from(atob(padded), (char) => char.codePointAt(0) ?? 0));
};

export const decodeClaims = (accessToken: string): Claims => {
  const payload = accessToken.split('.')[1];
  if (!payload) throw new Error('Malformed access token');
  return JSON.parse(decodeBase64Url(payload)) as Claims;
};

export const principalFromToken = (accessToken: string, clientId: string): Principal => {
  const claims = decodeClaims(accessToken);
  const organization = organizationOf(claims.organization);
  return {
    userId: claims.sub ?? '',
    name: claims.name ?? claims.preferred_username ?? '',
    email: claims.email ?? '',
    realmRoles: claims.realm_access?.roles ?? [],
    clientRoles: claims.resource_access?.[clientId]?.roles ?? [],
    tenantId: claims.tenant_id ?? firstValue(organization.attributes['tenant_id']),
    tenantSlug: claims.tenant_slug ?? firstValue(organization.attributes['tenant_slug']) ?? organization.slug,
    plan: claims.plan ?? firstValue(organization.attributes['plan']),
    features: claims.features ?? listValue(organization.attributes['features']),
    subscriptionStatus: claims.sub_status ?? firstValue(organization.attributes['sub_status']),
    expiresAt: (claims.exp ?? 0) * 1000,
  };
};

export const hasRole = (principal: Principal | null, role: string): boolean =>
  principal !== null && (principal.realmRoles.includes(role) || principal.clientRoles.includes(role));

export const hasFeature = (principal: Principal | null, feature: string): boolean =>
  principal?.features.includes(feature) ?? false;
